import { formatPresetTreeState } from 'components/presetManager/helpers/TreeHelperFunctions'
import { PresetModelContext } from 'context/preset-model/PresetModelContext'
import { useContext, useEffect, useState } from 'react'
import { TreeItem } from 'react-sortable-tree'
import { INodeWithPreset } from 'ts/interfaces'
import { parsePresetNodes } from '../utils/presetNodeParser'

interface UseTreeStateProps {
    presetId: number
    rootNode: any
    handleNodeClick: (nodeId: any, parentId: any) => void
    refetchPresetNodesData: (variables: any) => Promise<any>
    presetNodesData: any
}

// Utility function to find a node in the tree by ID
const findNodeInTree = (tree: TreeItem[], nodeId: any): TreeItem | null => {
    for (const treeNode of tree) {
        if (treeNode.id === nodeId) {
            return treeNode
        }
        if (treeNode.children && Array.isArray(treeNode.children)) {
            const found = findNodeInTree(treeNode.children, nodeId)
            if (found) return found
        }
    }
    return null
}

export const useTreeState = ({
    presetId,
    rootNode,
    handleNodeClick,
    refetchPresetNodesData,
    presetNodesData
}: UseTreeStateProps) => {
    const [uniqueKey, setUniqueKey] = useState<number>(0)
    const [syncing, setSyncing] = useState<boolean>(false)
    const [treeState, setTreeState] = useState<TreeItem[] | null>()
    const { state: presetModelState, setState: setPresetModelState } =
        useContext(PresetModelContext)

    // Single initialization effect - only runs when rootNode changes
    useEffect(() => {
        if (rootNode) {
            initializeTreeDirectly(rootNode)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootNode])

    // Handle re-render requests from context
    useEffect(() => {
        if (presetModelState.reRenderTree) {
            setPresetModelState((prev) => ({ ...prev, reRenderTree: false }))
            reRenderTree()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetModelState.reRenderTree])

    // Direct initialization without complex state management
    const initializeTreeDirectly = async (rootNode: any) => {
        const tree = [formatPresetTreeState(rootNode, !!presetModelState.hasEdited)]
        const parsedTree = await parsePresetNodes(
            tree,
            presetId,
            refetchPresetNodesData,
            presetNodesData
        )

        // Update both local and context state directly
        setTreeState(parsedTree)
        setPresetModelState((prev) => ({
            ...prev,
            treeState: parsedTree,
            reRenderTree: false
        }))

        // Auto-select root node if needed
        if (!presetModelState.selectedNode?.id || !presetModelState.hasEdited) {
            const rootNodeId = parsedTree[0]?.id
            if (rootNodeId) {
                handleNodeClick(rootNodeId, 'null')
                setPresetModelState((prev) => ({
                    ...prev,
                    selectedNode: parsedTree[0] as INodeWithPreset,
                    reRenderForm: true
                }))
            }
        }
    }

    // Direct re-render without complex state management
    const reRenderTree = async () => {
        setSyncing(true)
        try {
            const currentTree = presetModelState.treeState
            if (currentTree) {
                const updatedTree = await parsePresetNodes(
                    currentTree,
                    presetId,
                    refetchPresetNodesData,
                    presetNodesData
                )
                setTreeState(updatedTree)

                // Update the selected node if it exists
                let updatedSelectedNode = presetModelState.selectedNode
                if (presetModelState.selectedNode?.id) {
                    const foundNode = findNodeInTree(updatedTree, presetModelState.selectedNode.id)
                    if (foundNode) {
                        updatedSelectedNode = foundNode as INodeWithPreset
                    }
                }

                setPresetModelState((prev) => ({
                    ...prev,
                    treeState: updatedTree,
                    selectedNode: updatedSelectedNode
                }))
            }
        } catch (error) {
            console.error('Error during tree re-render:', error)
        } finally {
            setSyncing(false)
            setUniqueKey((prev) => prev + 1)
        }
    }

    // Handle node click directly without complex state synchronization
    const handleNodeSelection = async (node: TreeItem) => {
        handleNodeClick(node.id, node.parent_id)

        // Update context immediately
        setPresetModelState((prev) => ({
            ...prev,
            selectedNode: node as INodeWithPreset,
            reRenderForm: true
        }))

        // If node needs loading, do it immediately
        if (node.hasPresetLoading) {
            const currentTree = presetModelState.treeState || treeState
            if (currentTree) {
                const updatedTree = await parsePresetNodes(
                    currentTree,
                    presetId,
                    refetchPresetNodesData,
                    presetNodesData
                )
                setTreeState(updatedTree)
                setPresetModelState((prev) => ({
                    ...prev,
                    treeState: updatedTree
                }))
            }
        }
    }

    const handleTreeChange = (treeData: TreeItem[]) => {
        setTreeState(treeData)
        setPresetModelState((prev) => ({
            ...prev,
            hasChanges: true,
            treeState: treeData
        }))
    }

    return {
        treeState,
        syncing,
        uniqueKey,
        handleNodeSelection,
        handleTreeChange,
        reRenderTree,
        setTreeState
    }
}
